import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, LogOut, Shield, BarChart3, Settings, ChevronRight } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import DesktopSidebar from '../components/DesktopSidebar';

/**
 * Lightweight profile page. Shows user identity, role, and quick links
 * to admin/utility pages (admins only). Logout button.
 */
export default function ProfilePage({ user, onLogout }) {
  const navigate = useNavigate();
  const isAdmin = !!user?.is_admin;
  const name = user?.name || user?.email || 'Member';
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '').join('') || 'S';

  function handleLogout() {
    try { if (onLogout) onLogout(); } catch {}
    navigate('/login');
  }

  const adminLinks = [
    { label: 'Admin Dashboard',        icon: Shield,    path: '/admin' },
    { label: 'Analytics',              icon: BarChart3, path: '/admin/analytics' },
    { label: 'Document Governance',    icon: Settings,  path: '/admin/document-governance' },
  ];

  return (
    <div className="prf-root" data-testid="profile-page">
      <style>{styles}</style>
      <DesktopSidebar user={user} />

      <header className="prf-header">
        <button
          type="button"
          className="prf-back"
          onClick={() => navigate('/')}
          aria-label="Back"
          data-testid="profile-back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="prf-header-text">
          <User size={18} className="prf-header-icon" />
          <span>Profile</span>
        </div>
      </header>

      <main className="prf-main">
        <section className="prf-card" data-testid="profile-identity">
          <div className="prf-avatar">{initials}</div>
          <div className="prf-id-text">
            <div className="prf-name">{name}</div>
            {user?.email && <div className="prf-email">{user.email}</div>}
            <div className="prf-role-row">
              <span className={`prf-role ${isAdmin ? 'prf-role--admin' : ''}`}>
                {isAdmin ? 'Administrator' : 'Member'}
              </span>
              {user?.department && <span className="prf-meta">· {user.department}</span>}
            </div>
          </div>
        </section>

        <section className="prf-section" data-testid="profile-shortcuts">
          <div className="prf-section-title">Tools</div>
          <ul className="prf-link-list">
            <li>
              <button type="button" className="prf-link" onClick={() => navigate('/command-center')} data-testid="profile-link-command-center">
                <span>Command Center (all tools)</span>
                <ChevronRight size={14} />
              </button>
            </li>
            <li>
              <button type="button" className="prf-link" onClick={() => navigate('/conduct-predictor')} data-testid="profile-link-conduct">
                <span>Discipline Predictor</span>
                <ChevronRight size={14} />
              </button>
            </li>
            <li>
              <button type="button" className="prf-link" onClick={() => navigate('/grievance-builder')} data-testid="profile-link-grievance">
                <span>Grievance Builder</span>
                <ChevronRight size={14} />
              </button>
            </li>
            <li>
              <button type="button" className="prf-link" onClick={() => navigate('/job-briefing')} data-testid="profile-link-briefing">
                <span>Job Briefing Builder</span>
                <ChevronRight size={14} />
              </button>
            </li>
            <li>
              <button type="button" className="prf-link" onClick={() => navigate('/worker-voice')} data-testid="profile-link-worker-voice">
                <span>Worker Voice</span>
                <ChevronRight size={14} />
              </button>
            </li>
          </ul>
        </section>

        {isAdmin && (
          <section className="prf-section" data-testid="profile-admin">
            <div className="prf-section-title">Admin</div>
            <ul className="prf-link-list">
              {adminLinks.map((l) => {
                const Icon = l.icon;
                return (
                  <li key={l.path}>
                    <button type="button" className="prf-link" onClick={() => navigate(l.path)} data-testid={`profile-admin-${l.path.split('/').pop()}`}>
                      <Icon size={14} />
                      <span>{l.label}</span>
                      <ChevronRight size={14} className="prf-link-arrow" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <button type="button" className="prf-logout" onClick={handleLogout} data-testid="profile-logout">
          <LogOut size={16} />
          <span>Log out</span>
        </button>

        <div className="prf-bottom-spacer" />
      </main>

      <BottomNav />
    </div>
  );
}

const styles = `
.prf-root {
  min-height: 100vh;
  background: linear-gradient(180deg, #050507 0%, #0a0a0e 100%);
  color: #e4e4e7;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif;
  padding-bottom: 80px;
}
.prf-header {
  position: sticky; top: 0; z-index: 10;
  display: flex; align-items: center; gap: 10px;
  padding: 14px 18px;
  background: rgba(10,10,14,0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255,255,255,0.05);
  max-width: 540px; margin: 0 auto;
}
.prf-back {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  color: #d4d4d8; cursor: pointer;
  display: grid; place-items: center;
}
.prf-back:hover { background: rgba(255,255,255,0.08); }
.prf-header-text {
  display: flex; align-items: center; gap: 8px;
  font-size: 17px; font-weight: 700; color: #f4f4f5;
}
.prf-header-icon { color: #22d3ee; }

.prf-main {
  max-width: 540px; margin: 0 auto; padding: 16px 18px;
  display: flex; flex-direction: column; gap: 16px;
}

.prf-card {
  display: flex; align-items: center; gap: 14px;
  padding: 16px;
  background: rgba(24,24,27,0.7);
  border: 1px solid rgba(34,211,238,0.25);
  border-radius: 16px;
}
.prf-avatar {
  width: 56px; height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%);
  color: #0a0a0e;
  font-size: 20px; font-weight: 800;
  display: grid; place-items: center;
  flex-shrink: 0;
  box-shadow: 0 0 18px rgba(34,211,238,0.35);
}
.prf-id-text { flex: 1; min-width: 0; }
.prf-name {
  font-size: 16px; font-weight: 700; color: #f4f4f5;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.prf-email {
  margin-top: 2px;
  font-size: 12px; color: #a1a1aa;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.prf-role-row {
  margin-top: 8px;
  display: flex; align-items: center; gap: 6px;
}
.prf-role {
  padding: 2px 8px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 5px;
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: #a1a1aa;
}
.prf-role--admin {
  background: rgba(34,211,238,0.12);
  border-color: rgba(34,211,238,0.45);
  color: #67e8f9;
}
.prf-meta { font-size: 11px; color: #71717a; }

.prf-section { display: flex; flex-direction: column; gap: 8px; }
.prf-section-title {
  padding: 0 4px;
  font-size: 11px; font-weight: 700;
  letter-spacing: 0.12em; color: #71717a;
}
.prf-link-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px; }
.prf-link {
  width: 100%;
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px;
  background: rgba(24,24,27,0.6);
  border: 1px solid rgba(63,63,70,0.4);
  border-radius: 12px;
  color: #e4e4e7;
  font-size: 13px; font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
}
.prf-link:hover { border-color: rgba(34,211,238,0.4); background: rgba(34,211,238,0.04); }
.prf-link > span:first-of-type, .prf-link > span:not(.prf-link-arrow) { flex: 1; }
.prf-link svg:first-child { color: #22d3ee; }
.prf-link svg:last-child, .prf-link-arrow { color: #52525b; margin-left: auto; }

.prf-logout {
  margin-top: 8px;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px 16px;
  background: rgba(239,68,68,0.08);
  border: 1px solid rgba(239,68,68,0.35);
  border-radius: 12px;
  color: #f87171;
  font-size: 13px; font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.prf-logout:hover { background: rgba(239,68,68,0.18); border-color: rgba(239,68,68,0.55); }
.prf-bottom-spacer { height: 16px; }
`;
